<template>
  <modal
    ref="modal"
    full-width
    is-simplified-modal
    :id="modalId"
    :show-footer="false"
    :show-title-tabs="false"
    :show-header="false"
    :show-divider="false"
    @close="$emit('close')"
    size="flex"
    close-on-blur
  >
    <template v-slot:body>
      <div class="conversational-body" :class="{ 'panel-closed': !isSidePanelOpen }">
        <DsButton
          class="close-button"
          size="small"
          icon="x"
          icon-fill-color="black"
          variant="minimal"
          style="z-index: 150;"
          @click="close"
        />
        <div class="conversational-body__conversations-list">
          <ConversationalAgentSidePanel
            ref="sidePanel"
            :active-conversation-id="activeConversationId"
            @select-conversation="onSelectConversation"
            @create-conversation="createNewConversation"
            @toggle-panel="toggleSidePanel"
          />
        </div>

        <div class="conversational-main">
          <div class="conversational-main-wrapper" :style="{gap: '1rem'}">
            <ConversationalAgentChat
              :messages="messages"
              :is-thinking="isLoading"
              @typing-complete="isTyping = false"
              @scroll-down="scrollToBottom"
            />
            <ConversationalAgentInput
              :is-loading="isLoading"
              @send-message="sendUserMessage"
            />
          </div>
        </div>
      </div>
    </template>
  </modal>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import MODAL_IDS from '@/constants/modal-ids'
  import ConversationalAgentMixin from '@/Domain/ConversationalAgent/Mixins/ConversationalAgentMixin'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import Modal from '@/components/Modals/Modal.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import DsTextarea from '@/components/Form/DsTextarea.vue'
  import Avatar from '@/components/Avatar/Avatar.vue'
  import UserMixin from '@/util/UserMixin'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { getConversationMessages, sendMessage } from '@/Domain/ConversationalAgent/Api/conversationalAgent'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UiMixin from '@/util/UiMixin'
  import ConversationalAgentSidePanel from '@/Domain/ConversationalAgent/Components/ConversationalAgentSidePanel.vue'
  import ConversationalAgentChat from '@/Domain/ConversationalAgent/Components/ConversationalAgentChat.vue'
  import ConversationalAgentInput from '@/Domain/ConversationalAgent/Components/ConversationalAgentInput.vue'
  import { Message } from '@/Domain/ConversationalAgent/Types/types'

  export default defineComponent({
    name: "ConversationalAgentModal",
    components: {
      ConversationalAgentSidePanel,
      ConversationalAgentChat,
      ConversationalAgentInput,
      Avatar,
      DsTextarea,
      DsInput,
      Loading,
      Icon,
      Modal,
      FormGroup
    },
    emits: ['create-conversation'],
    mixins: [ConversationalAgentMixin, UserMixin, TranslationsMixin, UiMixin],
    data() {
      return {
        modalId: MODAL_IDS.PORTFOLIO_ACTORS_REMOVE,
        activeConversationId: '',
        isLoading: false,
        isTyping: false,
        isSidePanelOpen: true,
        showHumanContact: false,
        messages: [] as Message[]
      }
    },
    methods: {
      createNewConversation() {
        this.activeConversationId = 'new';
        this.messages = [];

        // Add new conversation to the conversations array
        const newConversation = {
          id: 'new',
          title: 'New Exploration',
          lastMessage: '',
          timestamp: new Date().toISOString(),
          session_id: null,
          messages: [],
          isActive: true
        };

        this.$refs.sidePanel.conversations.unshift(newConversation);
        this.onSelectConversation(newConversation.id, false);

        // Focus on the input field
        this.$nextTick(() => {
          const input = this.$el.querySelector('.input-textarea');
          if (input) {
            (input as HTMLTextAreaElement).focus();
          }
        });
      },
      onSelectConversation(id: string, fetchMessages = true) {
        this.activeConversationId = id;

        if (fetchMessages) {
          this.fetchConversationMessages();
        }
      },
      sendUserMessage(message: string) {
        if (!message || this.isLoading) return;

        const userMessage: Message = {
          id: Date.now().toString(),
          message: message,
          role: 'user',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };

        this.showHumanContact = false;
        this.messages.push(userMessage);
        this.isLoading = true;

        setTimeout(() => {
          this.scrollToBottom();
        }, 100)

        // Send message to backend
        sendMessage(message, this.activeConversationId === 'new' ? '' : this.activeConversationId)
          .then(async (response) => {
            const botMessage: Message = {
              id: (Date.now() + 1).toString(),
              message: response.message,
              role: 'assistant',
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              isTyping: true
            };

            this.messages.push(botMessage);
            this.isTyping = true;
            this.showHumanContact = response.metadata.show_human_contact;

            // If this was a new conversation, update the conversation list
            if (this.activeConversationId === 'new') {
              await this.$refs.sidePanel.fetchConversations();
              setTimeout(async () => {
                await this.onSelectConversation(response.id, false);
              }, 200)
            }
          })
          .catch(error => {
            console.error('Error sending message:', error);
            this.messages.push({
              id: (Date.now() + 2).toString(),
              message: 'Something went wrong. Please try again. If the problem persists, please contact support.',
              role: 'assistant',
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            });
          })
          .finally(() => {
            this.isLoading = false;
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          });
      },
      scrollToBottom() {
        const chatContainer = this.$el.querySelector('.chat-messages');
        if (chatContainer) {
          chatContainer.scrollTo({
            top: chatContainer.scrollHeight,
            behavior: 'smooth',
            duration: 300
          });
        }
      },
      close() {
        this.$emit('close')
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, null)
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.CONVERSATIONAL_AGENT)
      },
      fetchConversationMessages() {
        if (!this.activeConversationId) {
          this.messages = []
          return
        }
        getConversationMessages(this.activeConversationId)
          .then((response) => {
            this.messages = response
            // Set scroll position immediately without animation when switching conversations
            this.$nextTick(() => {
              const chatContainer = this.$el.querySelector('.chat-messages');
              if (chatContainer) {
                chatContainer.scrollTop = chatContainer.scrollHeight;
              }
            });
          })
      },
      toggleSidePanel() {
        this.isSidePanelOpen = !this.isSidePanelOpen;
        setTimeout(() => {
          this.scrollToBottom();
        }, 200);
      },
    },
    mounted() {
      if (this.$refs.sidePanel) {
        (this.$refs.sidePanel as any).fetchConversations().then(() => {
          const conversations = (this.$refs.sidePanel as any).conversations;
          if (conversations.length > 0) {
            this.onSelectConversation(String(conversations[0].session_id ?? conversations[0].id));
          } else {
            this.createNewConversation();
          }
        });
      }
    },
    watch: {}
  })
</script>

<style scoped lang="scss">
  .conversational-body {
    display: grid;
    grid-template-columns: 250px 1fr;
    max-width: 100%;
    position: relative;
    width: 90vw;
    height: 80vh;
    transition: all 0.3s ease;
    overflow: hidden;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

    .close-button {
      position: absolute;
      top: 0.25rem;
      right: -0.25rem;
      z-index: 150;
    }
  }

  .conversational-body.panel-closed {
    grid-template-columns: 0px 1fr;
  }

  .conversational-main {
    width: 100%;
    transition: all 0.3s ease;
    padding: 1rem 0.5rem 0.5rem;
    height: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
    background-color: #fff;
  }

  .conversational-main-wrapper {
    gap: 0.2rem;
    width: 100%;
    transition: all 0.3s ease;
    height: 100%;
    flex: 1;
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 770px;
    margin: 0 auto;
    justify-content: space-between;
  }

  .conversational-body__conversations-list {
    background-color: #fff;
    box-shadow: 2px 0 6px rgba(0, 0, 0, 0.1);
    overflow-y: visible;
    overflow-x: visible;
    width: 250px;
    position: relative;
    z-index: 2;
    transition: transform 0.3s ease;
    will-change: transform;
    height: 100%;
    border-right: 1px solid #f0f0f0;

    .conversational-body.panel-closed & {
      transform: translateX(-250px);
    }

    &[style*="display: none"], &[style*="display:none"] {
      visibility: hidden !important;
    }
  }
</style>
